if (!customElements.get('product-rerender')) {
  customElements.define(
    'product-rerender',
    class ProductRerender extends HTMLElement {
      connectedCallback() {
        this.handleRerender = this.handleRerender.bind(this);
        this.handlePopState = this.handlePopState.bind(this);

        this.addEventListener('product:rerender', this.handleRerender);
        window.addEventListener('popstate', this.handlePopState);
      }

      disconnectedCallback() {
        this.removeEventListener('product:rerender', this.handleRerender);
        window.removeEventListener('popstate', this.handlePopState);
      }

      handleRerender(event) {
        const url = event.detail && event.detail.url;
        if (!url) return;
        this.render(url, true);
      }

      handlePopState() {
        this.render(window.location.href, false);
      }

      async render(url, updateHistory) {
        if (this.isLoading) return;
        this.isLoading = true;
        this.classList.add('is-loading');

        try {
          const response = await fetch(url);
          if (!response.ok) return;
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newRoot = doc.querySelector('product-rerender');
          if (!newRoot) return;

          const loadedScripts = new Set([...document.querySelectorAll('script[src]')].map((s) => s.src));
          this.innerHTML = newRoot.innerHTML;
          this.loadMissingScripts(this, loadedScripts);
          this.remountAppBlocks();
          this.renderDependentSections(doc);

          if (updateHistory && window.location.href !== url) {
            window.history.pushState({}, '', url);
          }

          if (doc.title) document.title = doc.title;

          document.dispatchEvent(new CustomEvent('product:rerendered', { detail: { url } }));
        } catch (error) {
          console.error('product-rerender: failed to load', url, error);
        } finally {
          this.isLoading = false;
          this.classList.remove('is-loading');
        }
      }

      // Sections outside the product form that depend on product metafields (reviews, instafeed)
      renderDependentSections(doc) {
        document.querySelectorAll('.product-rerender-section').forEach((section) => {
          const newSection = doc.getElementById(section.id);
          if (!newSection) return;

          section.innerHTML = newSection.innerHTML;
          this.runScripts(section);
        });
      }

      // Blocks that weren't on the previous product (e.g. size picker grid on a single-size product)
      // bring scripts that were never loaded. Load only those, so already-run scripts don't bind twice.
      loadMissingScripts(container, loaded) {
        container.querySelectorAll('script[src]').forEach((oldScript) => {
          if (loaded.has(oldScript.src)) return;
          loaded.add(oldScript.src);

          const script = document.createElement('script');
          [...oldScript.attributes].forEach((attr) => script.setAttribute(attr.name, attr.value));
          script.async = false;
          oldScript.replaceWith(script);
        });
      }

      // App blocks (tcustomizer behind the Personalize button) set their product data in inline scripts
      // and build the widget client-side. Re-run those scripts, then ask tcustomizer to remount, otherwise
      // it stays bound to the previous product.
      remountAppBlocks() {
        this.querySelectorAll('.shopify-app-block script:not([src])').forEach((oldScript) => {
          const script = document.createElement('script');
          [...oldScript.attributes].forEach((attr) => script.setAttribute(attr.name, attr.value));
          script.textContent = oldScript.textContent;
          oldScript.replaceWith(script);
        });

        const variantInput = this.querySelector('form[action*="/cart/add"] [name="id"]');
        document.dispatchEvent(
          new CustomEvent('tcustomizer-remount', {
            detail: { selectedVariantId: variantInput ? variantInput.value : undefined },
          })
        );
      }

      // Scripts inserted via innerHTML don't execute, so recreate them
      runScripts(container) {
        container.querySelectorAll('script[src]').forEach((oldScript) => {
          const script = document.createElement('script');
          [...oldScript.attributes].forEach((attr) => script.setAttribute(attr.name, attr.value));
          oldScript.replaceWith(script);
        });
      }
    }
  );
}
